import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Tiny WebAuthn wrapper used by the keep-alive hook + the enrollment
 * panel. All calls are credentialed cookie auth — the backend
 * (webauthn_auth.py) verifies challenges + stores credentials on
 * users.webauthn_credentials.
 *
 * Defensive on every browser API call — if PublicKeyCredential is
 * undefined or the platform authenticator is unavailable, every
 * helper returns a safe falsy/false so the caller can fall back to
 * the standard refresh CTA.
 */

export function isWebAuthnSupported() {
  return typeof window !== "undefined"
    && typeof window.PublicKeyCredential !== "undefined"
    && typeof navigator !== "undefined"
    && navigator.credentials
    && typeof navigator.credentials.get === "function";
}

export async function isPlatformAuthenticatorAvailable() {
  try {
    if (!isWebAuthnSupported()) return false;
    if (typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable !== "function") return false;
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

// ── Encoding helpers ─────────────────────────────────────────────────
function b64urlToBuf(s) {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const b = atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
  const buf = new Uint8Array(b.length);
  for (let i = 0; i < b.length; i++) buf[i] = b.charCodeAt(i);
  return buf.buffer;
}

function bufToB64url(buf) {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.byteLength; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// ── Has-credential probe ─────────────────────────────────────────────
export async function hasRegisteredCredential() {
  try {
    const { data } = await axios.get(
      `${API}/api/auth/webauthn/has-credential`,
      { withCredentials: true },
    );
    return !!data?.has_credential;
  } catch {
    return false;
  }
}

// ── Enrollment ──────────────────────────────────────────────────────
export async function enrollPasskey(label = "Passkey") {
  if (!isWebAuthnSupported()) throw new Error("This device doesn't support passkeys.");

  const { data: opts } = await axios.post(
    `${API}/api/auth/webauthn/register/begin`,
    {},
    { withCredentials: true },
  );

  // Decode the bits the browser needs as ArrayBuffers.
  const publicKey = {
    ...opts,
    challenge: b64urlToBuf(opts.challenge),
    user: { ...opts.user, id: b64urlToBuf(opts.user.id) },
    excludeCredentials: (opts.excludeCredentials || []).map((c) => ({
      ...c,
      id: b64urlToBuf(c.id),
    })),
  };

  const cred = await navigator.credentials.create({ publicKey });
  if (!cred) throw new Error("Enrollment cancelled.");

  const credential = {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      attestationObject: bufToB64url(cred.response.attestationObject),
      clientDataJSON: bufToB64url(cred.response.clientDataJSON),
    },
    clientExtensionResults: cred.getClientExtensionResults?.() || {},
  };

  const transports = (cred.response.getTransports?.() || []);

  const { data: completed } = await axios.post(
    `${API}/api/auth/webauthn/register/complete`,
    { credential, transports, label },
    { withCredentials: true },
  );
  return completed;
}

// ── Verification (the soft-refresh used by the keep-alive hook) ─────
export async function verifyWithPasskey() {
  if (!isWebAuthnSupported()) throw new Error("This device doesn't support passkeys.");

  const { data: opts } = await axios.post(
    `${API}/api/auth/webauthn/verify/begin`,
    {},
    { withCredentials: true },
  );

  const publicKey = {
    ...opts,
    challenge: b64urlToBuf(opts.challenge),
    allowCredentials: (opts.allowCredentials || []).map((c) => ({
      ...c,
      id: b64urlToBuf(c.id),
    })),
  };

  const assertion = await navigator.credentials.get({ publicKey });
  if (!assertion) throw new Error("Verification cancelled.");

  const credential = {
    id: assertion.id,
    rawId: bufToB64url(assertion.rawId),
    type: assertion.type,
    response: {
      authenticatorData: bufToB64url(assertion.response.authenticatorData),
      clientDataJSON: bufToB64url(assertion.response.clientDataJSON),
      signature: bufToB64url(assertion.response.signature),
      userHandle: assertion.response.userHandle ? bufToB64url(assertion.response.userHandle) : null,
    },
    clientExtensionResults: assertion.getClientExtensionResults?.() || {},
  };

  const { data } = await axios.post(
    `${API}/api/auth/webauthn/verify/complete`,
    { credential },
    { withCredentials: true },
  );
  return data;
}

export async function listPasskeys() {
  try {
    const { data } = await axios.get(
      `${API}/api/auth/webauthn/has-credential`,
      { withCredentials: true },
    );
    return data;
  } catch {
    return { has_credential: false, count: 0 };
  }
}

export async function deletePasskey(credentialId) {
  await axios.delete(
    `${API}/api/auth/webauthn/credentials/${encodeURIComponent(credentialId)}`,
    { withCredentials: true },
  );
}
